import { useState } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal, ModalFooter } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { TwoFactorSetupModal } from './TwoFactorSetupModal';
import { RecoveryCodesDisplay } from './RecoveryCodesDisplay';
import {
  disable2FA,
  get2FAStatus,
  regenerateRecoveryCodes,
} from '../../api/two-factor';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function TwoFactorSettings() {
  const queryClient = useQueryClient();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showRecoveryCodesModal, setShowRecoveryCodesModal] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[]>([]);

  const { data: status, isLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: get2FAStatus,
  });

  const handleSetupSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
    queryClient.invalidateQueries({ queryKey: ['user'] });
  };

  const handleDisableSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
    queryClient.invalidateQueries({ queryKey: ['user'] });
    setShowDisableModal(false);
  };

  const handleRegenerateSuccess = (codes: string[]) => {
    queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
    setNewRecoveryCodes(codes);
    setShowRegenerateModal(false);
    setShowRecoveryCodesModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between py-3">
        <div>
          <p className="text-body-sm font-medium text-text-primary">Two-factor authentication</p>
          <p className="text-body-xs text-text-tertiary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between py-3">
        <div>
          <p className="text-body-sm font-medium text-text-primary">Two-factor authentication</p>
          <p className="text-body-xs text-text-tertiary">
            {status?.enabled
              ? `Enabled (${status.recoveryCodesRemaining} recovery codes remaining)`
              : 'Add an extra layer of security'}
          </p>
          {status?.required && !status?.enabled && (
            <p className="text-body-xs text-status-error mt-1">
              2FA is required by your administrator
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {status?.enabled ? (
            <>
              <Badge variant="success" size="md">Enabled</Badge>
              <Button variant="outline" size="sm" onClick={() => setShowRegenerateModal(true)}>
                Regenerate codes
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowDisableModal(true)}>
                Disable
              </Button>
            </>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setShowSetupModal(true)}>
              Enable 2FA
            </Button>
          )}
        </div>
      </div>

      <TwoFactorSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSuccess={handleSetupSuccess}
      />

      <DisableTwoFactorModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onSuccess={handleDisableSuccess}
      />

      <RegenerateRecoveryCodesModal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onSuccess={handleRegenerateSuccess}
      />

      <Modal
        isOpen={showRecoveryCodesModal}
        onClose={() => {
          setShowRecoveryCodesModal(false);
          setNewRecoveryCodes([]);
        }}
        title="New recovery codes"
        size="md"
      >
        <div className="space-y-4">
          <Alert variant="warning">
            Your old recovery codes have been invalidated. Save these new codes securely.
          </Alert>
          <RecoveryCodesDisplay codes={newRecoveryCodes} showWarning={false} />
          <ModalFooter>
            <Button
              variant="primary"
              onClick={() => {
                setShowRecoveryCodesModal(false);
                setNewRecoveryCodes([]);
              }}
            >
              Done
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
}

function DisableTwoFactorModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => disable2FA(password, token),
    onSuccess: () => {
      setPassword('');
      setToken('');
      setError(null);
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  const handleClose = () => {
    setPassword('');
    setToken('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Disable two-factor authentication" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Alert variant="warning">
          Disabling 2FA will make your account less secure. You'll need to set it up again if you want to re-enable it.
        </Alert>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Input
          label="Current password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <Input
          label="Verification code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={token}
          onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          required
        />

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={!password || token.length !== 6 || mutation.isPending}
          >
            {mutation.isPending ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function RegenerateRecoveryCodesModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (codes: string[]) => void;
}) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => regenerateRecoveryCodes(token),
    onSuccess: (response) => {
      setToken('');
      setError(null);
      onSuccess(response.recoveryCodes);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to regenerate codes');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  const handleClose = () => {
    setToken('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Regenerate recovery codes" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Alert variant="warning">
          This will invalidate all your existing recovery codes. Make sure to save the new codes after regenerating.
        </Alert>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Input
          label="Verification code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          value={token}
          onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          autoFocus
          required
        />

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={token.length !== 6 || mutation.isPending}
          >
            {mutation.isPending ? 'Regenerating...' : 'Regenerate codes'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
